/* External */
import {
  useCallback, useState, useRef, useEffect,
} from 'react';

export default function useShowAndHide(initialState, animationTimeMs = 1000) {
  const [mounted, setMountedOriginal] = useState(initialState);
  const [displaying, setDisplayingOriginal] = useState(initialState);
  const timeout = useRef({ display: undefined, mount: undefined });
  const refs = useRef({ displaying, mounted });

  const setMounted = useCallback((value) => {
    setMountedOriginal(value);
    refs.current.mounted = value;
  }, []);

  const setDisplaying = useCallback((value) => {
    setDisplayingOriginal(value);
    refs.current.displaying = value;
  }, []);

  const clearCurrentMountTimeout = useCallback(() => {
    if (timeout.current.mount) clearTimeout(timeout.current.mount);
  }, []);

  const clearCurrentDisplayTimeout = useCallback(() => {
    if (timeout.current.display) clearTimeout(timeout.current.display);
  }, []);

  const show = useCallback((withoutAnimation = false) => {
    clearCurrentMountTimeout();
    if (withoutAnimation === true) {
      setDisplaying(true);
    } else {
      timeout.current.display = setTimeout(() => {
        setDisplaying(true);
        timeout.current.display = undefined;
      }, 0);
    }
    setMounted(true);
  }, [clearCurrentMountTimeout, setDisplaying, setMounted]);

  const hide = useCallback(() => {
    setDisplaying(false);
    clearCurrentDisplayTimeout();
    timeout.current.mount = setTimeout(() => {
      setMounted(false);
      timeout.current.mount = undefined;
    }, animationTimeMs);
  }, [animationTimeMs, clearCurrentDisplayTimeout, setDisplaying, setMounted]);

  useEffect(() => () => {
    clearCurrentMountTimeout();
    clearCurrentDisplayTimeout();
  }, [clearCurrentDisplayTimeout, clearCurrentMountTimeout]);

  return {
    mounted,
    displaying,
    refs,
    show,
    hide,
  };
}
